import * as React from 'react';
import { AlertTriangle } from 'lucide-react';
import { usePermission } from '@tyndo/hooks';
import { WidgetCard, WidgetStateView } from '../widget-card';
import { useWidgetData } from '../widget-contract';
import type { WidgetDescriptor, WidgetSize } from '../widget-contract';
import {
  toneBadgeClass,
  toneTextClass,
  severityTone,
  type ExceptionModel,
  type ExceptionItem,
  type ExceptionSeverityCounts,
  type WidgetPermissionRef,
} from './models';

/**
 * `CanonicalExceptionWidget` — the reusable **exception / risk** kind.
 *
 * Renders an open-exception count, an optional per-severity chip row, and a
 * top-N drill-through list. The caller supplies the descriptor (REAL endpoint +
 * drill-through), the permission, and a `select` projector mapping the real API
 * response to an `ExceptionModel`.
 *
 * States derive from `useWidgetData` over the real query. A zero-exception
 * response renders the genuine empty ("all clear") state.
 */
export interface CanonicalExceptionWidgetProps<TRaw> {
  descriptor: WidgetDescriptor;
  permission: WidgetPermissionRef;
  select: (raw: TRaw) => ExceptionModel;
  isEmpty?: (raw: TRaw | null | undefined) => boolean;
  widgetSize?: WidgetSize;
  icon?: React.ReactNode;
  emptyTitle?: string;
  emptyDescription?: string;
}

const SEVERITY_LABEL: Array<{ key: keyof ExceptionSeverityCounts; label: string; tone: 'critical' | 'warning' | 'muted' }> = [
  { key: 'critical', label: 'Critical', tone: 'critical' },
  { key: 'major', label: 'Major', tone: 'warning' },
  { key: 'minor', label: 'Minor', tone: 'muted' },
];

function SeverityChips({ counts }: { counts: ExceptionSeverityCounts }): React.ReactElement {
  return (
    <div className="flex flex-wrap gap-1" data-testid="exception-severity-chips">
      {SEVERITY_LABEL.filter((s) => (counts[s.key] ?? 0) > 0).map((s) => (
        <span
          key={s.key}
          className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${toneBadgeClass(s.tone)}`}
          data-testid={`exception-severity-${s.key}`}
        >
          {counts[s.key]} {s.label}
        </span>
      ))}
    </div>
  );
}

function ExceptionRow({ item }: { item: ExceptionItem }): React.ReactElement {
  const tone = severityTone(item.severity);
  return (
    <li
      className="flex items-center justify-between gap-2 rounded border border-border/40 bg-muted/30 p-1.5"
      data-testid={`exception-row-${item.id}`}
    >
      <span className="min-w-0 truncate text-xs font-medium" title={item.primary}>
        {item.primary}
      </span>
      <span className="flex shrink-0 items-center gap-2 text-[11px] text-muted-foreground">
        {item.secondary ? (
          <span className={`truncate ${toneTextClass(tone)}`} title={item.secondary}>
            {item.secondary}
          </span>
        ) : null}
        {item.valueLabel ? <span className="tabular-nums">{item.valueLabel}</span> : null}
      </span>
    </li>
  );
}

export function CanonicalExceptionWidget<TRaw>({
  descriptor,
  permission,
  select,
  isEmpty,
  widgetSize = 'small',
  icon,
  emptyTitle = 'All clear',
  emptyDescription = 'No open exceptions right now.',
}: CanonicalExceptionWidgetProps<TRaw>): React.ReactElement {
  const perm = usePermission(permission.resource, permission.action);

  const state = useWidgetData<TRaw, ExceptionModel>({
    widgetId: descriptor.widgetId,
    dataSource: descriptor.dataSource,
    cachePolicy: 'memory',
    permissionDenied: !perm.loading && !perm.allowed,
    project: (raw) => select(raw as TRaw),
    isEmpty: isEmpty as ((d: TRaw | null | undefined) => boolean) | undefined,
  });

  return (
    <WidgetCard
      descriptor={descriptor}
      source={state.status === 'ready' ? state.source : undefined}
      onRefresh={state.refetch}
      icon={icon ?? <AlertTriangle className="h-4 w-4" aria-hidden />}
    >
      <WidgetStateView
        state={state}
        emptyTitle={emptyTitle}
        emptyDescription={emptyDescription}
        onRetry={state.refetch}
      >
        {(data) => (
          <div className="flex h-full flex-col gap-3" data-testid="exception-widget-body">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div
                  className="text-3xl font-bold tabular-nums text-warning"
                  data-testid="exception-widget-total"
                >
                  {data.total}
                </div>
                <div className="text-xs text-muted-foreground">
                  {data.unit ?? 'open exceptions'}
                </div>
              </div>
              {data.severityCounts ? <SeverityChips counts={data.severityCounts} /> : null}
            </div>
            {widgetSize !== 'small' && data.items.length > 0 ? (
              <ul className="flex flex-col gap-1" data-testid="exception-widget-list">
                {data.items.map((item) => (
                  <ExceptionRow key={item.id} item={item} />
                ))}
              </ul>
            ) : null}
          </div>
        )}
      </WidgetStateView>
    </WidgetCard>
  );
}

export default CanonicalExceptionWidget;
